import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, User, Phone, MessageSquare, ArrowLeft, Send, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const ContactsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  
  const { t, i18n } = useTranslation();

  // Detect current language from URL and update i18n
  React.useEffect(() => {
    const lang = location.pathname.includes('/en') ? 'en' : 'ru';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('/api/contacts/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Ошибка при отправке сообщения');
      }

      const result = await response.json();
      
      toast.success(t('contacts.successMessage'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contacts.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageSwitch = () => {
    const newLang = i18n.language === 'ru' ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
    if (newLang === 'ru') {
      navigate('/contacts');
    } else {
      navigate('/contacts/en');
    }
  };

  const backToHomePath = i18n.language === 'ru' ? '/' : '/en';

  return (
    <div className="min-h-screen paper-texture" data-testid="contacts-page">
      {/* Header */}
      <header className="border-b-2 border-black tech-lines bg-[#F5F1E8]" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to={backToHomePath} className="inline-flex items-center gap-2 hover:text-[#C41E3A] transition-colors" data-testid="back-home-link">
              <ArrowLeft size={20} />
              {t('contacts.backToHome')}
            </Link>
            
            {/* Language Switcher */}
            <button
              onClick={handleLanguageSwitch}
              className="flex items-center gap-2 text-lg hover:text-[#C41E3A] transition-colors"
              aria-label={i18n.language === 'ru' ? t('language.switchTo') : t('language.switchTo')}
            >
              <Globe size={20} />
              <span className="hidden sm:inline">
                {i18n.language === 'ru' ? 'EN' : 'RU'}
              </span>
              <span className="sm:hidden">
                {i18n.language === 'ru' ? t('language.en') : t('language.ru')}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1
            className="text-5xl font-bold mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="page-title"
          >
            <span className="accent-red">{t('contacts.title')}</span>
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto" data-testid="page-subtitle">
            {t('contacts.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Контактная информация */}
          <div>
            <div className="vintage-border bg-[#F5F1E8] p-8 mb-8">
              <h2
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="contact-info-title"
              >
                {t('contacts.contactInfoTitle')}
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-person">
                  <div className="w-12 h-12 bg-[#C41E3A] rounded-full flex items-center justify-center flex-shrink-0">
                    <User size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{t('contacts.contactPersonTitle')}</h3>
                    <p className="text-gray-700 text-lg">{t('contacts.contactPerson')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#C41E3A] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{t('contacts.emailTitle')}</h3>
                    <a
                      href="mailto:tjb.ai@yandex.ru"
                      className="text-gray-700 text-lg hover:text-[#C41E3A] transition-colors break-all"
                    >
                      {t('contacts.emailAddress')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-response">
                  <div className="w-12 h-12 bg-[#C41E3A] rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{t('contacts.innTitle')}</h3>
                    <p className="text-gray-700">{t('contacts.innNumber')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Дополнительная информация */}
            <div className="vintage-border bg-[#E8E3D5] p-6">
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('contacts.aboutTitle')}
              </h3>
              <p className="text-gray-700 mb-4">
                {t('contacts.aboutText1')}
              </p>
              <p className="text-gray-700">
                {t('contacts.aboutText2')}
              </p>
            </div>
          </div>

          {/* Форма обратной связи */}
          <div>
            <div className="vintage-border bg-[#F5F1E8] p-8">
              <h2
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="form-title"
              >
                {t('contacts.formTitle')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block mb-2 font-semibold" data-testid="name-label">
                    <User className="inline mr-2" size={18} />
                    {t('contacts.nameLabel')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                    placeholder={t('contacts.namePlaceholder')}
                    required
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold" data-testid="email-label">
                    <Mail className="inline mr-2" size={18} />
                    {t('contacts.emailLabel')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                    placeholder={t('contacts.emailPlaceholder')}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold" data-testid="phone-label">
                    <Phone className="inline mr-2" size={18} />
                    {t('contacts.phoneLabel')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                    placeholder={t('contacts.phonePlaceholder')}
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold" data-testid="message-label">
                    <MessageSquare className="inline mr-2" size={18} />
                    {t('contacts.messageLabel')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors resize-vertical"
                    placeholder={t('contacts.messagePlaceholder')}
                    required
                    data-testid="message-input"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full engraved-button engraved-button-red disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    t('contacts.sending')
                  ) : (
                    <>
                      <Send size={20} />
                      {t('contacts.sendButton')}
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Информация о конфиденциальности */}
            <div className="mt-6 p-4 bg-[#E8E3D5] border-2 border-black text-center">
              <p className="text-sm text-gray-700">
                {t('contacts.privacyText')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsPage;